import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';

function ReportAgent() {
  const navigate = useNavigate();
  const [tableName, setTableName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableInfos, setTableInfos] = useState({});
  const [fsSpec, setFsSpec] = useState('');
  const [tsSpec, setTsSpec] = useState('');
  const [activeTab, setActiveTab] = useState('fs');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState('');
  const [generating, setGenerating] = useState(false);
  const [customReq, setCustomReq] = useState('');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = React.useRef(null);
  const [refineInput, setRefineInput] = useState('');
  const [refining, setRefining] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [mode, setMode] = useState('single');
  const [batchFile, setBatchFile] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [showPrompts, setShowPrompts] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptSaving, setPromptSaving] = useState({});
  const [showSapConfig, setShowSapConfig] = useState(false);
  const [sapProfiles, setSapProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [sapForm, setSapForm] = useState({name:'',host:'',port:30015,username:'',password:'',description:''});
  const [sapTesting, setSapTesting] = useState(false);
  const [sapTestResult, setSapTestResult] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [promptHistory, setPromptHistory] = useState({});
  const [showHistory, setShowHistory] = useState(null);
  const [saveMemo, setSaveMemo] = useState('');

  const startTimer = () => { setElapsed(0); timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000); };
  const stopTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };

  const handleBatchUpload = async () => {
    if (!batchFile) return alert('엑셀 파일을 선택해주세요.');
    setBatchRunning(true);
    setBatchProgress(null);
    const formData = new FormData();
    formData.append('file', batchFile);
    try {
      const res = await fetch(`${API}/report/batch-upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) { alert(data.error); setBatchRunning(false); return; }
      // Poll progress
      const bid = data.batch_id;
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`${API}/report/batch-progress/${bid}`);
          const pg = await pr.json();
          setBatchProgress(pg);
          if (pg.status === 'done') {
            clearInterval(poll);
            setBatchRunning(false);
          }
        } catch (e) { clearInterval(poll); setBatchRunning(false); }
      }, 3000);
    } catch (e) { alert('업로드 실패'); setBatchRunning(false); }
  };

  const downloadTemplate = () => { window.open(`${API}/report/batch-template`, '_blank'); };

  const loadPrompts = async () => {
    try {
      const res = await fetch(`${API}/prompts`);
      const data = await res.json();
      setPrompts(data.prompts || []);
    } catch (e) {}
  };

  const savePrompt = async (key, value) => {
    setPromptSaving(p => ({...p, [key]: true}));
    try {
      await fetch(`${API}/prompts/${key}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({value, memo: saveMemo || '수동 저장'}) });
      setSaveMemo('');
    } catch (e) { alert('저장 실패'); }
    setPromptSaving(p => ({...p, [key]: false}));
  };

  const resetPrompt = async (key) => {
    if (!window.confirm('기본값으로 초기화하시겠습니까? (현재값은 히스토리에 백업됩니다)')) return;
    try {
      await fetch(`${API}/prompts/${key}/reset`, { method: 'POST' });
      loadPrompts();
    } catch (e) {}
  };

  const loadHistory = async (key) => {
    try {
      const res = await fetch(`${API}/prompts/${key}/history`);
      const data = await res.json();
      setPromptHistory(p => ({...p, [key]: data.history || []}));
      setShowHistory(showHistory === key ? null : key);
    } catch (e) {}
  };

  const restoreHistory = (key, value) => {
    setPrompts(prev => prev.map(p => p.key === key ? {...p, value} : p));
    setShowHistory(null);
  };

  const openPrompts = () => { setShowPrompts(true); loadPrompts(); };

  // SAP Profile Management
  const loadSapProfiles = async () => {
    try {
      const res = await fetch(`${API}/sap/profiles`);
      const data = await res.json();
      setSapProfiles(data.profiles || []);
      const def = (data.profiles || []).find(p => p.is_default);
      if (def && !activeProfile) setActiveProfile(def.id);
    } catch (e) {}
  };

  const saveSapProfile = async () => {
    if (!sapForm.name || !sapForm.host) return alert('이름과 Host를 입력해주세요.');
    const url = editingProfile ? `${API}/sap/profiles/${editingProfile}` : `${API}/sap/profiles`;
    const method = editingProfile ? 'PUT' : 'POST';
    await fetch(url, { method, headers: {'Content-Type':'application/json'}, body: JSON.stringify(sapForm) });
    setSapForm({name:'',host:'',port:30015,username:'',password:'',description:''});
    setEditingProfile(null);
    loadSapProfiles();
  };

  const testSapProfile = async () => {
    setSapTesting(true); setSapTestResult(null);
    try {
      const res = await fetch(`${API}/sap/profiles/test`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(sapForm) });
      setSapTestResult(await res.json());
    } catch (e) { setSapTestResult({success:false,message:'테스트 실패'}); }
    setSapTesting(false);
  };

  const setDefault = async (id) => {
    await fetch(`${API}/sap/profiles/${id}/default`, { method: 'POST' });
    setActiveProfile(id);
    loadSapProfiles();
  };

  const deleteSapProfile = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await fetch(`${API}/sap/profiles/${id}`, { method: 'DELETE' });
    if (activeProfile === id) setActiveProfile(null);
    loadSapProfiles();
  };

  const editSapProfile = (p) => {
    setSapForm({name:p.name, host:p.host, port:p.port, username:p.username, password:'', description:p.description||''});
    setEditingProfile(p.id);
  };

  const openSapConfig = () => { setShowSapConfig(true); loadSapProfiles(); };

  React.useEffect(() => { loadSapProfiles(); }, []);

  const searchTables = async () => {
    if (!tableName.trim()) return;
    setLoading(true); setError(''); setSearchResults([]); startTimer();
    try {
      const profileParam = activeProfile ? `&profile_id=${activeProfile}` : '';
      const res = await fetch(`${API}/report/tables?search=${encodeURIComponent(tableName)}${profileParam}`);
      const data = await res.json();
      if (data.error) setError(data.error); else setSearchResults(data.tables || []);
    } catch (e) { setError('서버 연결 실패'); }
    stopTimer(); setLoading(false);
  };

  const toggleSelect = (name) => setSelectedTables(p => p.includes(name) ? p.filter(n => n !== name) : [...p, name]);
  const selectAll = () => setSelectedTables(selectedTables.length === searchResults.length ? [] : searchResults.map(t => t.name));

  const analyzeSelected = async () => {
    if (selectedTables.length === 0) return alert('테이블을 선택해주세요.');
    setAnalyzing(true); setTableInfos({}); setFsSpec(''); setTsSpec(''); setError(''); startTimer();
    const infos = {};
    for (let i = 0; i < selectedTables.length; i++) {
      setAnalyzeProgress(`${selectedTables[i]} 분석 중... (${i+1}/${selectedTables.length})`);
      try {
        const res = await fetch(`${API}/report/analyze`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({table: selectedTables[i], profile_id: activeProfile}) });
        const data = await res.json();
        if (!data.error) infos[selectedTables[i]] = data;
      } catch (e) {}
    }
    setTableInfos(infos); setAnalyzeProgress(''); stopTimer(); setAnalyzing(false);
  };

  const generateSpec = async () => {
    const infoList = Object.values(tableInfos);
    if (infoList.length === 0) return alert('먼저 테이블을 분석해주세요.');
    setGenerating(true); setFsSpec(''); setTsSpec(''); startTimer();
    let autoTitle = '';
    for (const type of ['FS', 'TS']) {
      try {
        const res = await fetch(`${API}/report/generate-spec`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({table_info_list: infoList, spec_type: type, custom_request: customReq}) });
        const data = await res.json();
        if (type === 'FS') { setFsSpec(data.spec || ''); if (data.title) autoTitle = data.title; }
        else setTsSpec(data.spec || '');
      } catch (e) { if (type === 'FS') setFsSpec('생성 실패'); else setTsSpec('생성 실패'); }
    }
    stopTimer(); setGenerating(false); setActiveTab('fs');
    setSaveTitle(autoTitle || Object.keys(tableInfos).join(', ') + ' 스펙정의서');
  };

  const handleRefine = async () => {
    if (!refineInput.trim()) return alert('보정 지시를 입력해주세요.');
    const currentSpec = activeTab === 'fs' ? fsSpec : tsSpec;
    setRefining(true);
    try {
      const res = await fetch(`${API}/report/refine`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({spec: currentSpec, instruction: refineInput, spec_type: activeTab.toUpperCase()}) });
      const data = await res.json();
      if (activeTab === 'fs') setFsSpec(data.spec || fsSpec); else setTsSpec(data.spec || tsSpec);
      setRefineInput('');
    } catch (e) { alert('보정 실패'); }
    setRefining(false);
  };

  const handleSave = async () => {
    if (!saveTitle.trim()) return alert('제목을 입력해주세요.');
    try {
      const res = await fetch(`${API}/spec/save`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({title: saveTitle, tables: Object.keys(tableInfos).join(', '), fs_spec: fsSpec, ts_spec: tsSpec}) });
      const data = await res.json();
      setSaveMsg(data.message || data.error);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg('저장 실패'); }
  };

  const handleDownload = async (format) => {
    const spec = activeTab === 'fs' ? fsSpec : tsSpec;
    const label = Object.keys(tableInfos).join('_') + '_' + activeTab.toUpperCase();
    try {
      const res = await fetch(`${API}/prepare-download`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({answer: spec, question: label, format}) });
      const prepData = await res.json(); if (prepData.file_id) { window.open(`${API}/get-download/${prepData.file_id}`, '_self'); return; }
    } catch (e) { alert('다운로드 실패'); }
  };

  const md = {
    h1: ({children}) => <h1 style={{marginTop:20,marginBottom:10,fontSize:20,color:'#1b5e20',borderBottom:'2px solid #c8e6c9',paddingBottom:6}}>{children}</h1>,
    h2: ({children}) => <h2 style={{marginTop:18,marginBottom:8,fontSize:17,color:'#1b5e20',borderBottom:'2px solid #e8f5e9',paddingBottom:4}}>{children}</h2>,
    h3: ({children}) => <h3 style={{marginTop:16,marginBottom:6,fontSize:15,color:'#2e7d32',borderLeft:'4px solid #2e7d32',paddingLeft:10}}>{children}</h3>,
    h4: ({children}) => <h4 style={{marginTop:12,marginBottom:4,fontSize:14,color:'#33691e'}}>{children}</h4>,
    strong: ({children}) => <strong style={{color:'#1b5e20'}}>{children}</strong>,
    p: ({children}) => <p style={{margin:'6px 0',lineHeight:1.7}}>{children}</p>,
    ul: ({children}) => <ul style={{paddingLeft:20,margin:'6px 0'}}>{children}</ul>,
    ol: ({children}) => <ol style={{paddingLeft:20,margin:'6px 0'}}>{children}</ol>,
    li: ({children}) => <li style={{marginBottom:3}}>{children}</li>,
    table: ({children}) => <div style={{overflowX:'auto',margin:'10px 0'}}><table style={{borderCollapse:'collapse',width:'100%',fontSize:13,border:'1px solid #c8e6c9'}}>{children}</table></div>,
    thead: ({children}) => <thead style={{background:'#e8f5e9'}}>{children}</thead>,
    th: ({children}) => <th style={{border:'1px solid #a5d6a7',padding:'8px 10px',background:'#e8f5e9',textAlign:'left',fontWeight:'bold',color:'#1b5e20',whiteSpace:'nowrap'}}>{children}</th>,
    td: ({children}) => <td style={{border:'1px solid #c8e6c9',padding:'7px 10px',whiteSpace:'pre-wrap'}}>{children}</td>,
    tr: ({children, ...props}) => <tr style={{borderBottom:'1px solid #e0e0e0'}} {...props}>{children}</tr>,
    code: ({children}) => <code style={{background:'#f1f8e9',padding:'2px 6px',borderRadius:4,fontSize:13,color:'#33691e'}}>{children}</code>,
    pre: ({children}) => {
      // Check if child is a code block with html class
      const child = React.Children.toArray(children)[0];
      if (child?.props?.className === 'language-html') {
        const htmlCode = child.props.children;
        return (
          <div style={{margin:'12px 0'}}>
            <div style={{padding:'8px 14px',background:'#1565c0',color:'white',borderRadius:'8px 8px 0 0',fontSize:12,fontWeight:'bold',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span>화면 미리보기 (HTML)</span>
              <button onClick={() => {const w=window.open('','_blank','width=1000,height=600');w.document.write(htmlCode);w.document.close();}}
                style={{padding:'3px 10px',background:'rgba(255,255,255,0.2)',color:'white',border:'1px solid rgba(255,255,255,0.3)',borderRadius:4,cursor:'pointer',fontSize:11}}>
                새 창에서 보기
              </button>
            </div>
            <div style={{border:'1px solid #ccc',borderTop:'none',borderRadius:'0 0 8px 8px',overflow:'hidden',background:'white'}}>
              <div dangerouslySetInnerHTML={{__html: htmlCode}} style={{padding:0}} />
            </div>
          </div>
        );
      }
      return <pre style={{background:'#f5f5f5',padding:12,borderRadius:6,overflowX:'auto',fontSize:13}}>{children}</pre>;
    },
  };

  const infoCount = Object.keys(tableInfos).length;

  return (
    <div style={{minHeight:'100vh',background:'#f5f7fa'}}>
      <div style={{background:'linear-gradient(135deg,#1b5e20,#2e7d32,#43a047)',padding:'14px 20px',display:'flex',alignItems:'center',gap:16,boxShadow:'0 2px 12px rgba(46,125,50,0.3)'}}>
        <button onClick={() => navigate('/')} style={{background:'rgba(255,255,255,0.15)',border:'none',color:'white',width:36,height:36,borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div>
          <div style={{color:'white',fontSize:18,fontWeight:'bold'}}>보고서 작성 에이전트</div>
          <div style={{color:'rgba(255,255,255,0.8)',fontSize:12}}>SAP ABAP FS/TS 스펙 정의서 자동 생성</div>
        </div>
        {/* 현재 접속 프로필 */}
        {sapProfiles.length > 0 && (
          <select value={activeProfile || ''} onChange={e => setActiveProfile(Number(e.target.value))}
            style={{padding:'6px 10px',borderRadius:6,border:'1px solid rgba(255,255,255,0.3)',background:'rgba(255,255,255,0.15)',color:'white',fontSize:12,cursor:'pointer'}}>
            {sapProfiles.map(p => (
              <option key={p.id} value={p.id} style={{color:'#333'}}>{p.name}</option>
            ))}
          </select>
        )}
        <button onClick={openSapConfig} title="SAP 접속 설정"
          style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'white',padding:'6px 14px',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 12h8M8 17h4"/>
          </svg>
          SAP 접속
        </button>
        <button onClick={openPrompts} title="AI 프롬프트 설정"
          style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'white',padding:'6px 14px',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          프롬프트 설정
        </button>
      </div>

      <div style={{maxWidth:1100,margin:'24px auto',padding:'0 20px 40px'}}>
        {/* 모드 전환 */}
        <div style={{display:'flex',background:'#e0e0e0',borderRadius:8,overflow:'hidden',marginBottom:20}}>
          <button onClick={() => setMode('single')} style={{flex:1,padding:12,border:'none',cursor:'pointer',fontSize:14,fontWeight:'bold',background:mode==='single'?'#2e7d32':'transparent',color:mode==='single'?'white':'#555'}}>개별 스펙 생성</button>
          <button onClick={() => setMode('batch')} style={{flex:1,padding:12,border:'none',cursor:'pointer',fontSize:14,fontWeight:'bold',background:mode==='batch'?'#2e7d32':'transparent',color:mode==='batch'?'white':'#555'}}>엑셀 일괄 업로드</button>
        </div>

        {/* 일괄 업로드 */}
        {mode === 'batch' && (
          <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:24,marginBottom:20}}>
            <h2 style={{marginTop:0,fontSize:17,color:'#1b5e20'}}>엑셀 일괄 업로드</h2>
            <p style={{color:'#666',fontSize:13,marginBottom:16}}>엑셀 파일에 저장제목, 테이블, 비고를 작성하여 업로드하면 자동으로 FS/TS 스펙을 생성하고 저장합니다.</p>

            <div style={{background:'#f1f8e9',borderRadius:8,padding:16,marginBottom:16,border:'1px solid #c8e6c9'}}>
              <div style={{fontSize:13,fontWeight:'bold',color:'#1b5e20',marginBottom:8}}>엑셀 형식</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#e8f5e9'}}>
                  <th style={{padding:'6px 10px',border:'1px solid #c8e6c9',textAlign:'left'}}>A: 저장제목</th>
                  <th style={{padding:'6px 10px',border:'1px solid #c8e6c9',textAlign:'left'}}>B: 테이블</th>
                  <th style={{padding:'6px 10px',border:'1px solid #c8e6c9',textAlign:'left'}}>C: 비고 (추가 요청사항)</th>
                </tr></thead>
                <tbody>
                  <tr><td style={{padding:'6px 10px',border:'1px solid #c8e6c9',color:'#666'}}>인사기본정보 조회 리포트</td>
                    <td style={{padding:'6px 10px',border:'1px solid #c8e6c9',fontFamily:'monospace',color:'#2e7d32'}}>PA0001, PA0002, PA0003</td>
                    <td style={{padding:'6px 10px',border:'1px solid #c8e6c9',color:'#666'}}>HR 모듈 관점 분석</td></tr>
                  <tr><td style={{padding:'6px 10px',border:'1px solid #c8e6c9',color:'#666'}}>회계전표 조회 프로그램</td>
                    <td style={{padding:'6px 10px',border:'1px solid #c8e6c9',fontFamily:'monospace',color:'#2e7d32'}}>BKPF, BSEG</td>
                    <td style={{padding:'6px 10px',border:'1px solid #c8e6c9',color:'#666'}}>FI 모듈, 전표 조회</td></tr>
                </tbody>
              </table>
            </div>

            <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
              <button onClick={downloadTemplate} style={{padding:'8px 16px',background:'#217346',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:'bold'}}>템플릿 다운로드</button>
              <input type="file" accept=".xlsx,.xls" onChange={e => setBatchFile(e.target.files[0])} style={{fontSize:13}} />
              <button onClick={handleBatchUpload} disabled={batchRunning || !batchFile}
                style={{padding:'8px 20px',background:(!batchFile||batchRunning)?'#ccc':'#1b5e20',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:'bold'}}>
                {batchRunning ? '처리중...' : '일괄 생성 시작'}
              </button>
            </div>

            {/* 진행률 */}
            {batchProgress && (
              <div style={{background:'#fafafa',borderRadius:8,padding:16,border:'1px solid #e0e0e0'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontWeight:'bold',color:'#1b5e20',fontSize:14}}>
                    {batchProgress.status === 'done' ? '완료!' : '처리 중...'}
                  </span>
                  <span style={{fontSize:24,fontWeight:'bold',color:'#2e7d32'}}>{batchProgress.percent}%</span>
                </div>
                {/* 프로그레스 바 */}
                <div style={{height:8,background:'#e8f5e9',borderRadius:4,overflow:'hidden',marginBottom:10}}>
                  <div style={{height:'100%',background:'linear-gradient(90deg,#2e7d32,#43a047)',borderRadius:4,transition:'width 0.5s ease',width:batchProgress.percent+'%'}} />
                </div>
                <div style={{fontSize:12,color:'#888',marginBottom:8}}>
                  {batchProgress.current} / {batchProgress.total} 완료
                  {batchProgress.current_name && batchProgress.status !== 'done' &&
                    <span style={{marginLeft:8,color:'#2e7d32'}}>현재: {batchProgress.current_name}</span>}
                </div>
                {/* 완료 결과 */}
                {batchProgress.results.length > 0 && (
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:12,fontWeight:'bold',color:'#333',marginBottom:4}}>생성 완료 ({batchProgress.results.length}건)</div>
                    {batchProgress.results.map((r,i) => (
                      <div key={i} style={{padding:'6px 10px',background:'#e8f5e9',borderRadius:4,marginBottom:4,fontSize:12,display:'flex',justifyContent:'space-between'}}>
                        <span><strong>{r.title}</strong> <span style={{color:'#888',fontFamily:'monospace'}}>{r.tables}</span></span>
                        <span style={{color:'#2e7d32'}}>ID: {r.id}</span>
                      </div>
                    ))}
                  </div>
                )}
                {batchProgress.errors.length > 0 && (
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:12,fontWeight:'bold',color:'#c62828',marginBottom:4}}>오류 ({batchProgress.errors.length}건)</div>
                    {batchProgress.errors.map((e,i) => (
                      <div key={i} style={{padding:'4px 10px',background:'#ffebee',borderRadius:4,marginBottom:2,fontSize:11,color:'#c62828'}}>{e}</div>
                    ))}
                  </div>
                )}
                {batchProgress.status === 'done' && (
                  <div style={{marginTop:12,textAlign:'center'}}>
                    <button onClick={() => navigate('/agent/spec')}
                      style={{padding:'8px 20px',background:'#1565c0',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:'bold'}}>
                      스펙정의서에서 확인하기
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {mode === 'single' && (<>
        {/* 1. 검색 */}
        <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:20,marginBottom:20}}>
          <h2 style={{marginTop:0,fontSize:17,color:'#1b5e20'}}>1. SAP HANA 테이블 검색</h2>
          <div style={{display:'flex',gap:10,marginBottom:12}}>
            <input type="text" value={tableName} onChange={e => setTableName(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === 'Enter') searchTables(); }}
              placeholder="테이블명 입력 (예: BKPF, EKKO  쉼표로 여러개 가능)"
              style={{flex:1,padding:10,fontSize:15,borderRadius:6,border:'1px solid #ccc',fontFamily:'monospace'}} />
            <button onClick={searchTables} disabled={loading}
              style={{padding:'10px 24px',background:loading?'#ccc':'#2e7d32',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:14,fontWeight:'bold',whiteSpace:'nowrap'}}>
              {loading ? '검색중...' : '검색'}
            </button>
          </div>
          {loading && (
            <div style={{display:'flex',alignItems:'center',gap:12,padding:16,background:'#f1f8e9',borderRadius:8,border:'1px solid #c8e6c9',marginBottom:12}}>
              <div style={{width:24,height:24,border:'3px solid #e8f5e9',borderTop:'3px solid #2e7d32',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
              <div><div style={{fontWeight:'bold',color:'#2e7d32',fontSize:14}}>SAP HANA 서버에서 검색 중...</div>
              <div style={{color:'#666',fontSize:12,marginTop:2}}>경과: {elapsed}초</div></div>
              <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
            </div>
          )}
          {error && <div style={{padding:10,background:'#ffebee',color:'#c62828',borderRadius:6,fontSize:13,marginBottom:12}}>{error}</div>}
          {searchResults.length > 0 && (
            <>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:13,color:'#666'}}>{searchResults.length}개 테이블 | <strong style={{color:'#2e7d32'}}>{selectedTables.length}개 선택</strong></span>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={selectAll} style={{padding:'4px 12px',background:'#f5f5f5',border:'1px solid #ddd',borderRadius:4,cursor:'pointer',fontSize:12}}>
                    {selectedTables.length === searchResults.length ? '전체 해제' : '전체 선택'}
                  </button>
                  <button onClick={analyzeSelected} disabled={analyzing || selectedTables.length === 0}
                    style={{padding:'4px 16px',background:selectedTables.length===0?'#ccc':'#2e7d32',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>
                    선택 테이블 분석 ({selectedTables.length})
                  </button>
                </div>
              </div>
              <div style={{maxHeight:280,overflowY:'auto',border:'1px solid #e0e0e0',borderRadius:6}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{background:'#e8f5e9',position:'sticky',top:0}}>
                    <th style={{padding:'8px 12px',width:40,textAlign:'center',borderBottom:'2px solid #2e7d32'}}>선택</th>
                    <th style={{padding:'8px 12px',textAlign:'left',borderBottom:'2px solid #2e7d32'}}>테이블명</th>
                    <th style={{padding:'8px 12px',textAlign:'center',borderBottom:'2px solid #2e7d32'}}>타입</th>
                    <th style={{padding:'8px 12px',textAlign:'right',borderBottom:'2px solid #2e7d32'}}>레코드</th>
                  </tr></thead>
                  <tbody>{searchResults.map((t,i) => (
                    <tr key={i} onClick={() => toggleSelect(t.name)} style={{borderBottom:'1px solid #eee',cursor:'pointer',background:selectedTables.includes(t.name)?'#e8f5e9':(i%2===0?'white':'#fafafa')}}>
                      <td style={{padding:'8px 12px',textAlign:'center'}}><input type="checkbox" checked={selectedTables.includes(t.name)} onChange={() => {}} /></td>
                      <td style={{padding:'8px 12px',fontFamily:'monospace',fontWeight:'bold'}}>{t.name}</td>
                      <td style={{padding:'8px 12px',textAlign:'center'}}>{t.type}</td>
                      <td style={{padding:'8px 12px',textAlign:'right'}}>{t.rows?.toLocaleString()}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* 분석 진행 */}
        {analyzing && (
          <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:24,marginBottom:20,textAlign:'center'}}>
            <div style={{width:40,height:40,border:'4px solid #e8f5e9',borderTop:'4px solid #2e7d32',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}}/>
            <div style={{fontWeight:'bold',color:'#2e7d32',fontSize:16}}>{analyzeProgress}</div>
            <div style={{color:'#888',fontSize:13,marginTop:6}}>경과: {elapsed}초</div>
          </div>
        )}

        {/* 2. 분석 완료 → 스펙 생성 */}
        {infoCount > 0 && (
          <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:20,marginBottom:20}}>
            <h2 style={{marginTop:0,fontSize:17,color:'#1b5e20'}}>2. 분석 완료 ({infoCount}개)</h2>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
              {Object.entries(tableInfos).map(([name,info]) => (
                <div key={name} style={{padding:'8px 14px',background:'#e8f5e9',borderRadius:8,border:'1px solid #c8e6c9'}}>
                  <span style={{fontFamily:'monospace',fontWeight:'bold',color:'#1b5e20'}}>{name}</span>
                  <span style={{fontSize:11,color:'#666',marginLeft:8}}>{info.columns?.length}cols</span>
                </div>
              ))}
            </div>
            <h2 style={{fontSize:17,color:'#1b5e20'}}>3. FS/TS 스펙 정의서 생성</h2>
            <div style={{marginBottom:12}}>
              <input type="text" value={customReq} onChange={e => setCustomReq(e.target.value)}
                placeholder="추가 요청 (선택, 예: FI 모듈 관점에서 분석)"
                style={{width:'100%',padding:10,borderRadius:6,border:'1px solid #ccc',fontSize:14,boxSizing:'border-box'}} />
            </div>
            <button onClick={generateSpec} disabled={generating}
              style={{padding:'12px 32px',background:generating?'#ccc':'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'white',border:'none',borderRadius:6,cursor:generating?'not-allowed':'pointer',fontSize:15,fontWeight:'bold'}}>
              {generating ? 'FS/TS 생성 중... ('+elapsed+'초)' : 'FS + TS 스펙 정의서 자동 생성'}
            </button>
          </div>
        )}

        {/* FS/TS 결과 */}
        {(fsSpec || tsSpec) && (
          <div style={{background:'white',borderRadius:8,border:'1px solid #ddd',overflow:'hidden',marginBottom:20}}>
            {/* 탭 */}
            <div style={{display:'flex',borderBottom:'2px solid #e0e0e0'}}>
              {[{k:'fs',l:'Functional Spec (FS)',c:'#2e7d32',bg:'#e8f5e9'},{k:'ts',l:'Technical Spec (TS)',c:'#1565c0',bg:'#e3f2fd'}].map(t => (
                <button key={t.k} onClick={() => setActiveTab(t.k)}
                  style={{flex:1,padding:14,border:'none',cursor:'pointer',fontSize:15,fontWeight:'bold',
                    background:activeTab===t.k?t.bg:'white',color:activeTab===t.k?t.c:'#999',
                    borderBottom:activeTab===t.k?'3px solid '+t.c:'3px solid transparent'}}>{t.l}</button>
              ))}
            </div>
            {/* 도구바 */}
            <div style={{padding:'10px 20px',background:'#fafafa',borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
              <span style={{fontWeight:'bold',color:'#333',fontSize:13}}>{Object.keys(tableInfos).join(', ')}</span>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                <button onClick={() => handleDownload('word')} style={{padding:'5px 12px',background:'#2b579a',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>Word</button>
                <button onClick={() => handleDownload('excel')} style={{padding:'5px 12px',background:'#217346',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>Excel</button>
                <button onClick={() => handleDownload('pdf')} style={{padding:'5px 12px',background:'#c62828',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>PDF</button>
                <button onClick={() => {navigator.clipboard.writeText(activeTab==='fs'?fsSpec:tsSpec);alert('복사됨');}}
                  style={{padding:'5px 12px',background:'#666',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>복사</button>
              </div>
            </div>
            {/* 본문 */}
            <div style={{padding:24,fontSize:14,lineHeight:1.8}}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{activeTab === 'fs' ? fsSpec : tsSpec}</ReactMarkdown>
            </div>
            {/* AI 보정 */}
            <div style={{padding:'16px 20px',borderTop:'1px solid #eee',background:'#fafafa'}}>
              <div style={{fontSize:13,fontWeight:'bold',color:'#333',marginBottom:8}}>AI 보정</div>
              <div style={{display:'flex',gap:10}}>
                <input type="text" value={refineInput} onChange={e => setRefineInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !refining) handleRefine(); }}
                  placeholder="보정 지시 (예: 테이블 설명을 더 상세하게, 테스트 케이스 추가해줘)"
                  style={{flex:1,padding:10,borderRadius:6,border:'1px solid #ccc',fontSize:14}} />
                <button onClick={handleRefine} disabled={refining}
                  style={{padding:'10px 20px',background:refining?'#ccc':'#e67700',color:'white',border:'none',borderRadius:6,cursor:refining?'not-allowed':'pointer',fontSize:14,fontWeight:'bold',whiteSpace:'nowrap'}}>
                  {refining ? '보정중...' : 'AI 보정'}
                </button>
              </div>
            </div>
            {/* 저장 */}
            <div style={{padding:'12px 20px',borderTop:'1px solid #eee',background:'#f5f5f5',display:'flex',gap:10,alignItems:'center'}}>
              <input type="text" value={saveTitle} onChange={e => setSaveTitle(e.target.value)}
                placeholder="저장 제목" style={{flex:1,padding:8,borderRadius:4,border:'1px solid #ccc',fontSize:13}} />
              <button onClick={handleSave} style={{padding:'8px 20px',background:'#1b5e20',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:13,fontWeight:'bold'}}>저장</button>
              {saveMsg && <span style={{fontSize:12,color:'#2e7d32'}}>{saveMsg}</span>}
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* 프롬프트 관리 모달 */}
      {showPrompts && (
        <div style={{position:'fixed',top:0,right:0,bottom:0,width:'100%',maxWidth:700,background:'white',boxShadow:'-4px 0 20px rgba(0,0,0,0.15)',zIndex:1000,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{background:'linear-gradient(135deg,#1b5e20,#2e7d32)',padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{color:'white',fontSize:17,fontWeight:'bold'}}>AI 프롬프트 설정</div>
              <div style={{color:'rgba(255,255,255,0.8)',fontSize:12}}>스펙 생성에 사용되는 AI 명령을 직접 편집할 수 있습니다</div>
            </div>
            <button onClick={() => setShowPrompts(false)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',width:32,height:32,borderRadius:'50%',cursor:'pointer',fontSize:16}}>X</button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:20}}>
            <div style={{background:'#f1f8e9',borderRadius:8,padding:14,marginBottom:20,border:'1px solid #c8e6c9',fontSize:13,color:'#33691e',lineHeight:1.6}}>
              <strong>사용법:</strong> 각 프롬프트를 편집하면 스펙 생성 시 해당 내용이 AI에게 전달됩니다.
              비어있으면 기본 프롬프트가 사용됩니다. FS/TS 템플릿에는 <code>{'{{context}}'}</code> 위치에 테이블 분석 데이터가 자동 삽입됩니다.
            </div>

            {prompts.map(p => (
              <div key={p.key} style={{background:'#fafafa',borderRadius:8,border:'1px solid #e0e0e0',padding:16,marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:'bold',fontSize:15,color:'#1b5e20'}}>{p.name}</div>
                    <div style={{fontSize:12,color:'#888'}}>{p.description} <span style={{color:'#bbb'}}>| key: {p.key}</span></div>
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button onClick={() => loadHistory(p.key)}
                      style={{padding:'4px 10px',background:showHistory===p.key?'#e3f2fd':'#f5f5f5',color:showHistory===p.key?'#1565c0':'#666',border:'1px solid '+(showHistory===p.key?'#90caf9':'#ddd'),borderRadius:4,cursor:'pointer',fontSize:11}}>
                      히스토리 {showHistory===p.key?'닫기':'열기'}
                    </button>
                    <button onClick={() => resetPrompt(p.key)}
                      style={{padding:'4px 10px',background:'#fff3e0',color:'#e67700',border:'1px solid #ffe0b2',borderRadius:4,cursor:'pointer',fontSize:11}}>
                      초기화
                    </button>
                    <button onClick={() => savePrompt(p.key, p.value)}
                      disabled={promptSaving[p.key]}
                      style={{padding:'4px 12px',background:promptSaving[p.key]?'#ccc':'#2e7d32',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:'bold'}}>
                      {promptSaving[p.key] ? '저장중...' : '저장'}
                    </button>
                  </div>
                </div>
                <textarea
                  value={p.value || ''}
                  onChange={e => setPrompts(prev => prev.map(pp => pp.key === p.key ? {...pp, value: e.target.value} : pp))}
                  placeholder={p.key === 'system' ?
                    '예: 너는 SAP 구축 프로젝트의 리딩 컨설턴트야. 10년 이상 경험...' :
                    p.key === 'fs_template' ?
                    '비어있으면 기본 FS 템플릿 사용. 커스텀 섹션 구조를 정의할 수 있습니다.' :
                    '비어있으면 기본 TS 템플릿 사용. 커스텀 섹션 구조를 정의할 수 있습니다.'
                  }
                  style={{
                    width:'100%',minHeight: p.key === 'system' ? 120 : 250,
                    padding:12,borderRadius:6,border:'1px solid #ddd',fontSize:13,
                    fontFamily:'monospace',lineHeight:1.6,boxSizing:'border-box',
                    resize:'vertical',background:'white'
                  }}
                />
                {/* 저장 메모 */}
                <div style={{display:'flex',gap:8,marginTop:8,alignItems:'center'}}>
                  <input type="text" value={saveMemo} onChange={e => setSaveMemo(e.target.value)}
                    placeholder="변경 사유 메모 (선택)"
                    style={{flex:1,padding:'6px 10px',borderRadius:4,border:'1px solid #ddd',fontSize:12}} />
                  <span style={{fontSize:11,color:'#bbb'}}>{p.updated_at ? '수정: '+p.updated_at : ''}</span>
                </div>
                {p.value === '' && (
                  <div style={{marginTop:6,fontSize:11,color:'#999',fontStyle:'italic'}}>
                    비어있음 - 기본 프롬프트가 사용됩니다
                  </div>
                )}

                {/* 히스토리 패널 */}
                {showHistory === p.key && (
                  <div style={{marginTop:12,background:'#f8fbff',borderRadius:8,border:'1px solid #e3f2fd',overflow:'hidden'}}>
                    <div style={{padding:'10px 14px',background:'#e3f2fd',fontWeight:'bold',fontSize:13,color:'#1565c0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span>프롬프트 히스토리 (최근 20건)</span>
                      <span style={{fontSize:11,color:'#888'}}>{(promptHistory[p.key]||[]).length}건</span>
                    </div>
                    {(!promptHistory[p.key] || promptHistory[p.key].length === 0) && (
                      <div style={{padding:16,textAlign:'center',color:'#999',fontSize:13}}>저장된 히스토리가 없습니다</div>
                    )}
                    <div style={{maxHeight:300,overflowY:'auto'}}>
                      {(promptHistory[p.key]||[]).map((h,hi) => (
                        <div key={h.id} style={{padding:'10px 14px',borderBottom:'1px solid #e8f0fe',display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10,
                          background:hi%2===0?'white':'#fafcff'}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
                              <span style={{fontSize:11,color:'#1565c0',fontWeight:'bold'}}>#{h.id}</span>
                              <span style={{fontSize:11,color:'#888'}}>{h.created_at}</span>
                              {h.memo && <span style={{fontSize:10,padding:'1px 6px',background:'#e8f5e9',color:'#2e7d32',borderRadius:8}}>{h.memo}</span>}
                            </div>
                            <div style={{fontSize:11,color:'#555',whiteSpace:'pre-wrap',maxHeight:60,overflow:'hidden',textOverflow:'ellipsis',fontFamily:'monospace',lineHeight:1.4}}>
                              {h.value.substring(0, 200)}{h.value.length > 200 ? '...' : ''}
                            </div>
                          </div>
                          <button onClick={() => restoreHistory(p.key, h.value)}
                            style={{padding:'4px 12px',background:'#1565c0',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:'bold',whiteSpace:'nowrap',flexShrink:0}}>
                            불러오기
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {showPrompts && <div onClick={() => setShowPrompts(false)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.3)',zIndex:999}} />}

      {/* SAP 접속 설정 패널 */}
      {showSapConfig && (
        <div style={{position:'fixed',top:0,right:0,bottom:0,width:'100%',maxWidth:550,background:'white',boxShadow:'-4px 0 20px rgba(0,0,0,0.15)',zIndex:1000,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{background:'linear-gradient(135deg,#1b5e20,#2e7d32)',padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{color:'white',fontSize:17,fontWeight:'bold'}}>SAP 접속 설정</div>
              <div style={{color:'rgba(255,255,255,0.8)',fontSize:12}}>SAP HANA DB 접속 프로필 관리</div>
            </div>
            <button onClick={() => setShowSapConfig(false)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',width:32,height:32,borderRadius:'50%',cursor:'pointer',fontSize:16}}>X</button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:20}}>
            {/* 등록된 프로필 */}
            <h3 style={{margin:'0 0 12px',fontSize:14,color:'#1b5e20'}}>등록된 접속 프로필 ({sapProfiles.length})</h3>
            {sapProfiles.map(p => (
              <div key={p.id} style={{padding:14,marginBottom:10,borderRadius:8,border:p.is_default?'2px solid #2e7d32':'1px solid #e0e0e0',background:p.is_default?'#f1f8e9':'white'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <div>
                    <span style={{fontWeight:'bold',fontSize:14,color:'#333'}}>{p.name}</span>
                    {p.is_default && <span style={{marginLeft:8,fontSize:10,padding:'2px 8px',background:'#2e7d32',color:'white',borderRadius:10}}>기본</span>}
                  </div>
                  <div style={{display:'flex',gap:4}}>
                    {!p.is_default && <button onClick={() => setDefault(p.id)} style={{padding:'3px 8px',background:'#e8f5e9',color:'#2e7d32',border:'1px solid #c8e6c9',borderRadius:4,cursor:'pointer',fontSize:10}}>기본설정</button>}
                    <button onClick={() => editSapProfile(p)} style={{padding:'3px 8px',background:'#e3f2fd',color:'#1565c0',border:'1px solid #90caf9',borderRadius:4,cursor:'pointer',fontSize:10}}>수정</button>
                    <button onClick={() => deleteSapProfile(p.id)} style={{padding:'3px 8px',background:'#ffebee',color:'#c62828',border:'1px solid #ef9a9a',borderRadius:4,cursor:'pointer',fontSize:10}}>삭제</button>
                  </div>
                </div>
                <div style={{fontSize:12,color:'#666',fontFamily:'monospace'}}>{p.host}:{p.port} / {p.username}</div>
                {p.description && <div style={{fontSize:11,color:'#888',marginTop:2}}>{p.description}</div>}
              </div>
            ))}

            {/* 신규/수정 폼 */}
            <h3 style={{margin:'20px 0 12px',fontSize:14,color:'#1b5e20'}}>{editingProfile ? '프로필 수정' : '새 접속 프로필 추가'}</h3>
            <div style={{background:'#fafafa',borderRadius:8,padding:16,border:'1px solid #e0e0e0'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div style={{gridColumn:'1/3'}}>
                  <label style={{fontSize:11,color:'#888'}}>프로필 이름 *</label>
                  <input value={sapForm.name} onChange={e => setSapForm({...sapForm, name: e.target.value})}
                    placeholder="예: SAP S/4HANA PRD" style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box'}} />
                </div>
                <div>
                  <label style={{fontSize:11,color:'#888'}}>Host *</label>
                  <input value={sapForm.host} onChange={e => setSapForm({...sapForm, host: e.target.value})}
                    placeholder="61.74.135.124" style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box',fontFamily:'monospace'}} />
                </div>
                <div>
                  <label style={{fontSize:11,color:'#888'}}>Port *</label>
                  <input type="number" value={sapForm.port} onChange={e => setSapForm({...sapForm, port: e.target.value})}
                    style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box',fontFamily:'monospace'}} />
                </div>
                <div>
                  <label style={{fontSize:11,color:'#888'}}>Username *</label>
                  <input value={sapForm.username} onChange={e => setSapForm({...sapForm, username: e.target.value})}
                    placeholder="SAPHANADB" style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box',fontFamily:'monospace'}} />
                </div>
                <div>
                  <label style={{fontSize:11,color:'#888'}}>Password *</label>
                  <input type="password" value={sapForm.password} onChange={e => setSapForm({...sapForm, password: e.target.value})}
                    placeholder={editingProfile ? '변경 시에만 입력' : ''} style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box'}} />
                </div>
                <div style={{gridColumn:'1/3'}}>
                  <label style={{fontSize:11,color:'#888'}}>설명 (선택)</label>
                  <input value={sapForm.description} onChange={e => setSapForm({...sapForm, description: e.target.value})}
                    placeholder="예: 개발서버, 운영서버 등" style={{width:'100%',padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:13,boxSizing:'border-box'}} />
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginTop:12,alignItems:'center'}}>
                <button onClick={testSapProfile} disabled={sapTesting}
                  style={{padding:'6px 14px',background:'#e67700',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>
                  {sapTesting ? '테스트중...' : '연결 테스트'}
                </button>
                <button onClick={saveSapProfile}
                  style={{padding:'6px 14px',background:'#2e7d32',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>
                  {editingProfile ? '수정 저장' : '프로필 추가'}
                </button>
                {editingProfile && (
                  <button onClick={() => { setEditingProfile(null); setSapForm({name:'',host:'',port:30015,username:'',password:'',description:''}); }}
                    style={{padding:'6px 14px',background:'#f5f5f5',color:'#666',border:'1px solid #ddd',borderRadius:4,cursor:'pointer',fontSize:12}}>취소</button>
                )}
                {sapTestResult && (
                  <span style={{fontSize:12,color:sapTestResult.success?'#2e7d32':'#c62828',fontWeight:'bold'}}>{sapTestResult.message}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSapConfig && <div onClick={() => setShowSapConfig(false)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.3)',zIndex:999}} />}
    </div>
  );
}

export default ReportAgent;
